import { apiInstance } from '@/api/api.js'
import { DEFAULT_LANGUAGE } from '../../helpers/constants'
import { debounce } from '@/helpers/debounce'
const api = apiInstance()

export default {
  async getPatients({ state, commit, dispatch }, params) {
    params = params || state.patientsFilters
    try {
      commit('setLoading', true)
      const response = await api.get('/participants', { params })
      commit('setPatientsFilters', params)
      commit('setPatients', response.data.participants)
      commit('appStates/setBreadcrumbCurrentView', '', { root: true })
      commit('setPagination', response.data._meta)
      commit('setLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async getDialogPatients({ state, commit, dispatch }, params) {
    params = params || state.dialogPatientsFilters
    try {
      commit('setLoading', true)
      const response = await api.get('/participants', { params })
      commit('setDialogPatientsFilters', params)
      commit('setDialogPatients', response.data.participants)
      commit('setDialogPagination', response.data._meta)
      commit('setLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async getPatient({ commit, dispatch }, id) {
    const url = `/participants/${id}`

    try {
      commit('setLoading', true)
      const response = await api.get(url)
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      commit('updateCurrentPatient', response.data)
      commit(
        'appStates/setBreadcrumbCurrentView',
        response.data.firstName + ' ' + response.data.lastName,
        { root: true },
      )
      commit('setLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async changePatientField(
    { state, dispatch, commit, rootState },
    { id, key, value },
  ) {
    // save old data
    const oldData = {
      ...state.currentPatient,
      language: state.currentPatient.language || DEFAULT_LANGUAGE,
    }

    const url = `/participants/${id}`

    try {
      const { headersStates } = rootState

      commit('setLoading', true)

      // prepare new data
      let patientData = {
        ...state.currentPatient,
        language: state.currentPatient.language || DEFAULT_LANGUAGE,
      }
      if (value == null) delete patientData[key]
      else
        patientData = {
          ...state.currentPatient,
          language: state.currentPatient.language || DEFAULT_LANGUAGE,
          [key]: value,
        }

      // commit update note
      commit('updateCurrentPatient', patientData)

      const response = await api.put(url, patientData, {
        headers: { 'If-Match': headersStates.headersEtag[url] },
      })
      await dispatch('getPatient', id)
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'Saved!', { root: true })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      // commit - restore old data
      commit('updateCurrentPatient', oldData)
      await dispatch('getPatient', id)
      commit('setLoading', false)
    }
  },
  async changePatientFeatureFlag(
    { state, dispatch, commit, rootState },
    { id, key, value },
  ) {
    const url = `/participants/${id}/featureFlag`
    const participantUrl = `/participants/${id}`

    try {
      const { headersStates } = rootState

      commit('setLoading', true)

      let patientFeatureFlagData = {
        journalAvailable: state.currentPatient.journalAvailable,
        smartSurveyAvailable: state.currentPatient.smartSurveyAvailable,
        swapAvailable: state.currentPatient.swapAvailable,
        surveyAvailable: state.currentPatient.surveyAvailable,
      }
      patientFeatureFlagData[key] = value

      const response = await api.put(url, patientFeatureFlagData, {
        headers: { 'If-Match': headersStates.headersEtag[participantUrl] },
      })
      await dispatch('getPatient', id)
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'Saved!', { root: true })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      await dispatch('getPatient', id)
      commit('setLoading', false)
    }
  },
  async addPatient({ state, dispatch, commit }, { data }) {
    try {
      commit('setLoading', true)
      const response = await api.post('/participants', {
        ...state.currentPatient,
        ...data,
      })
      await dispatch('getPatients')
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'Participant added!', {
        root: true,
      })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async getCalendar({ commit, dispatch }, params) {
    try {
      commit('setLoading', true)
      const response = await api.get('/journals/calendar', { params })
      commit('setCalendar', response.data)
      commit('setLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async getJournal({ commit, state, dispatch }, { id, date, loader = true }) {
    const url = `/journals/participants/${id}/date/${date}`
    clearTimeout(state.reloadJournalTimeout)
    try {
      if (loader) commit('setLoading', true)
      const response = await api.get(url)
      state.reloadJournalTimeout = setTimeout(() => {
        dispatch('getJournal', { id: id, date: date, loader: false })
      }, 60000 * 4)
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      commit('setJournal', response.data)
      commit('setLoading', false)
    } catch (error) {
      // commit('appStates/setSnackbarNotification', 'Please try again! '+error.message, { root: true })
      commit('setJournal', false)
      commit('setLoading', false)
    }
  },
  async changeJournal(
    { state, commit, rootState, dispatch },
    { id, date, value },
  ) {
    const url = `/journals/participants/${id}/date/${date}`

    try {
      const { headersStates } = rootState
      commit('setLoading', true)
      const response = await api.put(
        url,
        { comment: value },
        { headers: { 'If-Match': headersStates.headersEtag[url] } },
      )
      commit('setJournal', response.data)
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'Saved!', { root: true })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async changeJournalMeal(
    { dispatch, state, commit, rootState },
    { id, date, meal },
  ) {
    try {
      const { headersStates } = rootState
      commit('setLoading', true)
      const response = await api.put(
        `/journals/participants/${id}/date/${date}/meals/${meal.id}`,
        meal,
        { headers: { 'If-Match': 'W/"' + meal.version + '"' } },
      )
      commit('setLoading', false)
      await dispatch('getJournal', { id, date })
      commit('appStates/setSnackbarNotification', 'Saved!', { root: true })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async addJournalMealComment(
    { dispatch, state, commit, rootState },
    { id, date, mealId, comment },
  ) {
    try {
      const { headersStates } = rootState
      commit('setLoading', true)
      const response = await api.post(
        `/journals/participants/${id}/date/${date}/meals/${mealId}/comments`,
        comment,
      )
      commit('setLoading', false)
      await dispatch('getJournal', { id, date })
      commit('appStates/setSnackbarNotification', 'Saved!', { root: true })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async getTimedReleaseMessagesCalendar({ commit, dispatch }, params) {
    const url = `/messages/users/${params.id}/calendar/preview`
    const newParamsWithoutId = {
      startDate: params.from,
      endDate: params.to,
    }
    try {
      commit('setLoading', true)
      const response = await api.get(url, { params: newParamsWithoutId })
      commit('setTimedReleaseMessagesDates', response.data)
      commit('setLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async getTimedReleaseMessages({ commit, dispatch }, params) {
    const url = `/messages/users/${params.id}`
    const newParamsWithoutId = {
      from: params.from,
      to: params.to,
    }
    try {
      commit('setLoading', true)
      const response = await api.get(url, { params: newParamsWithoutId })
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      commit('setTimedReleaseMessages', response.data)
      commit('setLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setTimedReleaseMessages', false)
      commit('setLoading', false)
    }
  },
  async getTimedReleaseMessagesDefinitions(
    { commit, dispatch },
    { id, params },
  ) {
    const url = `/messages/users/${id}/definitions`

    try {
      commit('setLoading', true)
      const response = await api.get(url, { params })
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      commit('setTimedReleaseMessagesDefinitions', response.data)
      commit('setLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setTimedReleaseMessages', false)
      commit('setLoading', false)
    }
  },
  async getTimedReleaseMessagesDefinition({ commit, dispatch }, { id, data }) {
    const url = `/messages/definitions/${id}`

    try {
      commit('setLoading', true)
      const response = await api.get(url, data)
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      commit('setTimedReleaseMessagesDefinitions', response.data)
      commit('setLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async addTimedReleaseMessagesDefinition(
    { commit, dispatch },
    { id, data, params },
  ) {
    const url = `/messages/users/${id}/definitions`

    let file = null

    const model = {
      ...data,
    }

    if (!data.thumbnailValue) {
      // do not change thumbnail
      model.resourceId = data?.resourceId
      model.staticMediaId = data?.staticMediaId
    } else if (data?.thumbnailValue?.remove) {
      // remove thumbnail
      model.resourceId = null
      model.staticMediaId = null
    } else if (data?.thumbnailValue?.mlSourceResourceId) {
      // select new thumbnail from media library
      model.staticMediaId = null
      model.resourceId = data?.thumbnailValue?.mlSourceResourceId
    } else if (data?.thumbnailValue?.staticMediaId) {
      // select new thumbnail form placeholders
      model.resourceId = null
      model.staticMediaId = data?.thumbnailValue?.staticMediaId
    } else {
      // upload new thumbnail from local device
      model.staticMediaId
      file = data.thumbnailValue
    }

    let formData = new FormData()

    if (file) {
      formData.append(
        'file',
        new Blob([file.file], {
          type: 'application/octet-stream',
        }),
        file.name,
      )

      // remove attach ml file if there is file upload from computer included
      delete data['media']
    }

    if (data.thumbnailValue) {
      delete data['thumbnailValue']
    }

    if (model.thumbnailValue) {
      delete model['thumbnailValue']
    }

    formData.append(
      'model',
      new Blob([JSON.stringify(model)], {
        type: 'application/json',
      }),
    )

    try {
      commit('setLoading', true)
      const response = await api.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      await dispatch('getTimedReleaseMessages', params)
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'Message definition added!', {
        root: true,
      })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async changeTimedReleaseMessagesDefinition(
    { state, commit, dispatch, rootState },
    { id, params, data },
  ) {
    const url = `/messages/definitions/${id}`
    try {
      const { headersStates } = rootState

      let file = null

      const model = {
        ...data,
      }

      if (!data.thumbnailValue) {
        // do not change thumbnail
        model.resourceId = data?.resourceId
        model.staticMediaId = data?.staticMediaId
      } else if (data?.thumbnailValue?.remove) {
        // remove thumbnail
        model.resourceId = null
        model.staticMediaId = null
      } else if (data?.thumbnailValue?.mlSourceResourceId) {
        // select new thumbnail from media library
        model.staticMediaId = null
        model.resourceId = data?.thumbnailValue?.mlSourceResourceId
      } else if (data?.thumbnailValue?.staticMediaId) {
        // select new thumbnail form placeholders
        model.resourceId = null
        model.staticMediaId = data?.thumbnailValue?.staticMediaId
      } else {
        // upload new thumbnail from local device
        model.staticMediaId = null
        file = data.thumbnailValue
      }

      let formData = new FormData()

      if (file) {
        formData.append(
          'file',
          new Blob([file.file], {
            type: 'application/octet-stream',
          }),
          file.name,
        )

        // remove attach ml file if there is file upload from computer included
        delete data['resourceId']
        delete data['staticMediaId']
      }

      if (data.thumbnailValue) {
        delete data['thumbnailValue']
      }

      if (model.thumbnailValue) {
        delete model['thumbnailValue']
      }

      formData.append(
        'model',
        new Blob([JSON.stringify(model)], {
          type: 'application/json',
        }),
      )

      commit('setLoading', true)
      await dispatch('getTimedReleaseMessagesDefinition', { id: id })
      const response = await api.put(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'If-Match': headersStates.headersEtag[url],
        },
      })
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      await dispatch('getTimedReleaseMessages', params)
      await dispatch('getTimedReleaseMessagesCalendar', params)
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'Saved!', { root: true })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
      throw error
    }
  },
  async splitTimedReleaseMessagesDefinition(
    { state, commit, dispatch, rootState },
    { id, data, params },
  ) {
    const url = `/messages/definitions/${id}`
    try {
      const { headersStates } = rootState

      commit('setLoading', true)
      await dispatch('getTimedReleaseMessagesDefinition', { id: id })
      const response = await api.post(url + '/split', data, {
        headers: { 'If-Match': headersStates.headersEtag[url] },
      })
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      await dispatch('getTimedReleaseMessages', params)
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'Saved!', { root: true })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async copyTimedReleaseMessagesDefinition(
    { state, commit, dispatch, rootState },
    { id, data, params },
  ) {
    const url = `/messages/definitions/${id}`
    try {
      commit('setLoading', true)
      await dispatch('getTimedReleaseMessagesDefinition', { id: id })
      await api.post(url + '/copy', data)
      await dispatch('getTimedReleaseMessages', params)
      commit('setLoading', false)
      commit(
        'appStates/setSnackbarNotification',
        'Message duplicated successfully!',
        { root: true },
      )
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async deleteTimedReleaseMessagesDefinition(
    { state, commit, dispatch, rootState },
    { id, params },
  ) {
    const url = `/messages/definitions/${id}`
    try {
      const { headersStates } = rootState

      commit('setLoading', true)
      await dispatch('getTimedReleaseMessagesDefinition', { id: id })
      const response = await api.delete(url, {
        headers: { 'If-Match': headersStates.headersEtag[url] },
      })
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      await dispatch('getTimedReleaseMessages', params)
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'Messages deleted!', {
        root: true,
      })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async deleteMessagesByCorrelationId(
    { state, commit, dispatch, rootState },
    { id, params },
  ) {
    const url = `/messages/definitions/correlationId/${id}`
    try {
      const { headersStates } = rootState

      commit('setLoading', true)
      const response = await api.delete(url)
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      await dispatch('getTimedReleaseMessages', params)
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'Messages deleted!', {
        root: true,
      })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async getTimedReleaseMessagesDefinitionResource(
    { state, commit, dispatch, rootState },
    { id, params },
  ) {
    const url = `/ml/resources/${id}`
    try {
      const { headersStates } = rootState

      commit('setLoading', true)
      const response = await api.get(url)
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      commit('setTimedReleaseMessagesDefinitionResource', response.data)
      commit('setLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async sendWelcomeEmail({ state, commit }, { participantId }) {
    const url = `/participants/${participantId}/sendWelcomeEmail`
    try {
      commit('setLoading', true)
      const response = await api.post(url)
      commit('setLoading', false)
      commit(
        'appStates/setSnackbarNotification',
        'Welcome e-mail successfully sent',
        { root: true },
      )
    } catch {
      commit(
        'appStates/setSnackbarNotification',
        'Error while sending Welcome Email',
        { root: true },
      )
    }
  },
  async resetParticipantPassword({ state, commit }, { participantId }) {
    const url = `/participants/${participantId}/passwordReset`
    try {
      commit('setLoading', true)
      const response = await api.post(url)
      commit('setLoading', false)
      commit(
        'appStates/setSnackbarNotification',
        "Participant's password has been successfully reset",
        { root: true },
      )
    } catch {
      commit(
        'appStates/setSnackbarNotification',
        "Participant's password reset failed",
        { root: true },
      )
    }
  },
  async createNewSmartSurvey({ state, commit }, { participantId }) {
    const url = `/participants/${participantId}/survey`
    try {
      commit('setLoading', true)
      const response = await api.post(url)
      commit('setLoading', false)
      commit(
        'appStates/setSnackbarNotification',
        'Success! Notification sent to Participant',
        { root: true },
      )
    } catch {
      commit(
        'appStates/setSnackbarNotification',
        'Error while sending Smart Survey notification',
        { root: true },
      )
    }
  },
  async getParticipantSmartSurvey({ state, commit }, { participantId }) {
    const url = `/participants/${participantId}/survey`
    try {
      const response = await api.get(url)
      return response.data
    } catch (error) {
      return error
    }
  },
  async getParticipantSmartSurveyImage(
    { state, commit },
    { participantId, mediaKind },
  ) {
    const url = `/participants/${participantId}/survey/media/${mediaKind}`

    try {
      const response = await api.get(url)
      return response.data
    } catch (error) {
      return error
    }
  },
  async uploadParticipants({ commit, dispatch }, { file, advisorId }) {
    try {
      commit('setLoading', true)
      let data = new FormData()
      data.append('advisorId', advisorId)
      data.append('file', file)

      const response = await api.post(`/participants/import`, data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      dispatch('userStates/getTasks', {}, { root: true })
      return response
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async getParticipantsUploadTemplate({ state, commit }) {
    const url = `/participants/import/template`
    try {
      const response = await api.get(url)
      return response.data
    } catch (error) {
      return error
    }
  },
  async getExercises(
    { commit, state, dispatch },
    { participantId, params, loader = true, retry = 1, maxRetry = 3 },
  ) {
    params = params || state.exercisesFilters

    const url = `/participants/${participantId}/routines/calendar`

    try {
      if (loader) commit('setLoading', true)
      const response = await api.get(url, { params })
      let processingMedia = response.data.days.filter(day => {
        let exercisesMedia = day.items.filter(exercise => {
          if (exercise.media && exercise.media.processing) return true
          else if (
            exercise.series?.exercises.findIndex(
              item => item.media?.processing,
            ) > -1
          )
            return true
          else if (exercise.series?.media?.processing) return true
          else if (exercise.exercise?.media?.processing) return true
          else if (exercise.activity?.media?.processing) return true
          else return false
        })
        if (exercisesMedia && exercisesMedia.length > 0) return true
        else return false
      })
      clearTimeout(state.reloadTimeoutExercises)
      if (processingMedia.length > 0 && retry) {
        console.log('processing found, retry ordered')
        let retNext = false
        let timeouts = [5, 25, 60]
        state.reloadTimeoutExercises = setTimeout(() => {
          if (retry < maxRetry) retNext = retry + 1
          dispatch('getExercises', {
            participantId: participantId,
            params: params,
            loader: false,
            retry: retNext,
            maxRetry: maxRetry,
          })
        }, timeouts[retry - 1] * 1000)
      }
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      commit('setExercises', response.data)
      commit('setExercisesFilters', params)
      commit('setLoading', false)
    } catch (error) {
      commit('setLoading', false)
    }
  },
  async getExercise(
    { commit, state, dispatch },
    { participantId, type, exerciseId, loader = true },
  ) {
    let typeUrl = ''
    switch (type) {
      case 'EXERCISE':
        typeUrl = 'exercises'
        break
      case 'SERIES':
        typeUrl = 'series'
        break
      case 'ACTIVITY':
        typeUrl = 'activities'
        break
      default:
        typeUrl = 'exercises'
        break
    }
    let url = `/participants/${participantId}/routines/${typeUrl}/${exerciseId}`
    try {
      if (loader) commit('setLoading', true)
      const response = await api.get(url)
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      response.data.type = type
      commit('setExercise', response.data)
      commit('setLoading', false)
    } catch (error) {
      commit('setLoading', false)
    }
  },
  async addParticipantExercise(
    { commit, dispatch, rootState },
    { participantId, exerciseDefinitionId, type, date },
  ) {
    let urlType = ''
    let label = ''
    let postObject = {
      date: date,
    }
    let modifiedExerciseId = exerciseDefinitionId
    if (
      typeof exerciseDefinitionId == 'string' &&
      exerciseDefinitionId.includes('__')
    )
      modifiedExerciseId = parseInt(exerciseDefinitionId.split('__')[1])
    switch (type) {
      case 'EXERCISE':
        urlType = 'exercises'
        label = 'Single exercise'
        postObject.exerciseDefinitionId = modifiedExerciseId
        break
      case 'SERIES':
        urlType = 'series'
        label = 'Circuit'
        postObject.exerciseDefinitionSeriesId = modifiedExerciseId
        break
      case 'ACTIVITY':
        urlType = 'activities'
        label = 'Activity'
        postObject.activityDefinitionId = modifiedExerciseId
        break
      default:
        dispatch('appStates/setErrorMessage', 'Please try again!', {
          root: true,
        })
        return
    }

    const url = `/participants/${participantId}/routines/${urlType}/import`

    try {
      const { headersStates } = rootState
      commit('setLoading', true)
      await api.post(url, postObject)
      await dispatch('getExercises', { participantId: participantId })
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', `${label} added!`, {
        root: true,
      })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async addParticipantExerciseDefinition(
    { commit, dispatch, rootState },
    { participantId, data },
  ) {
    let urlType = ''
    let label = ''
    switch (data.type) {
      case 'EXERCISE':
        urlType = 'exercises'
        label = 'Single exercise'
        break
      case 'SERIES':
        urlType = 'series'
        label = 'Circuit'
        if (!data.restTimeAfterExercise) data.restTimeAfterExercise = 0
        break
      case 'ACTIVITY':
        urlType = 'activities'
        label = 'Activity'
        break
      default:
        dispatch('appStates/setErrorMessage', 'Please try again!', {
          root: true,
        })
        return
    }

    const url = `/participants/${participantId}/routines/${urlType}`

    const {
      media = null,
      additionalMedia = null,
      currentAdditionalMedia,
      currentMedia,
      ...rest
    } = data

    const model = { ...rest }

    try {
      const { headersStates } = rootState
      commit('setLoading', true)
      let formData = new FormData()

      let file = null

      if (media === null || media === undefined) {
        // do not change thumbnail
        model.media = null
      } else if (media?.mlSourceResourceId) {
        // select new thumbnail from media library
        model.media = { mlSourceResourceId: media?.mlSourceResourceId }
      } else if (media?.remove) {
        // remove thumbnail
        model.media = { remove: true }
      } else {
        // upload new thumbnail from local device
        file = media.file
      }

      // Additional Media
      let additionalFile = null

      if (additionalMedia === null || additionalMedia === undefined) {
        // do not change thumbnail
        model.additionalMedia = null
      } else if (additionalMedia?.mlSourceResourceId) {
        // select new thumbnail from media library
        model.additionalMedia = {
          mlSourceResourceId: additionalMedia?.mlSourceResourceId,
        }
      } else if (additionalMedia?.remove) {
        // remove thumbnail
        model.additionalMedia = { remove: true }
      } else {
        // upload new thumbnail from local device
        additionalFile = additionalMedia.file
      }

      formData.append(
        'model',
        new Blob([JSON.stringify(model)], {
          type: 'application/json',
        }),
      )

      if (file) {
        formData.append(
          'file',
          new Blob([file], {
            type: 'application/octet-stream',
          }),
          media?.file?.name || file.name,
        )
      }

      if (additionalFile) {
        formData.append(
          'additionalFile',
          new Blob([additionalFile], {
            type: 'application/octet-stream',
          }),
          additionalMedia?.file?.name || additionalFile.name,
        )
      }

      const response = await api.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      await dispatch('getExercises', { participantId: participantId })
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', `${label} added!`, {
        root: true,
      })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async deleteParticipantExercise(
    { commit, dispatch, rootState },
    { participantId, type, exerciseId, inSeriesType = 'none' },
  ) {
    let typeUrl = ''
    switch (type) {
      case 'EXERCISE':
        typeUrl = 'exercises'
        break
      case 'SERIES':
        typeUrl = 'series'
        break
      case 'ACTIVITY':
        typeUrl = 'activities'
        break
      default:
        typeUrl = 'exercises'
        break
    }
    let url = `/participants/${participantId}/routines/${typeUrl}/${exerciseId}`

    if (inSeriesType === 'following') {
      url += '?following=true'
    } else if (inSeriesType === 'all') {
      url += '?all=true'
    }

    try {
      const { headersStates } = rootState
      commit('setLoading', true)
      await api.delete(url)
      await dispatch('getExercises', { participantId: participantId })
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'Item deleted!', {
        root: true,
      })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async deleteParticipantExercises(
    { commit, dispatch, rootState },
    { participantId, exercises = [] },
  ) {
    let url = `/participants/${participantId}/routines/items`

    url += '?id='

    exercises.forEach(id => {
      url += `${id},`
    })

    url = url.slice(0, -1)

    try {
      const { headersStates } = rootState
      commit('setLoading', true)
      await api.delete(url)
      await dispatch('getExercises', { participantId: participantId })
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'Items deleted!', {
        root: true,
      })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async removeAllParticipantDayExercises(
    { commit, dispatch, rootState },
    { participantId, day },
  ) {
    let url = `/participants/${participantId}/routines/calendar/items/date/${day}`

    try {
      const { headersStates } = rootState
      commit('setLoading', true)
      await api.delete(url)
      commit('setLoading', false)
      commit(
        'appStates/setSnackbarNotification',
        'All activities for the day deleted!',
        {
          root: true,
        },
      )
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async duplicateParticipantExercise(
    { commit, dispatch, rootState },
    { participantId, type, exerciseId },
  ) {
    let typeUrl = ''
    switch (type) {
      case 'EXERCISE':
        typeUrl = 'exercises'
        break
      case 'SERIES':
        typeUrl = 'series'
        break
      case 'ACTIVITY':
        typeUrl = 'activities'
        break
      default:
        typeUrl = 'exercises'
        break
    }
    let url = `/participants/${participantId}/routines/${typeUrl}/${exerciseId}/copy`

    try {
      const { headersStates } = rootState
      commit('setLoading', true)
      await api.post(url)
      await dispatch('getExercises', { participantId: participantId })
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'Item duplicated!', {
        root: true,
      })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async pasteParticipantExercise(
    { commit, dispatch, rootState },
    { participantId, type, exerciseId, date },
  ) {
    let typeUrl = ''
    switch (type) {
      case 'EXERCISE':
        typeUrl = 'exercises'
        break
      case 'SERIES':
        typeUrl = 'series'
        break
      case 'ACTIVITY':
        typeUrl = 'activities'
        break
      default:
        typeUrl = 'exercises'
        break
    }
    let url = `/participants/${participantId}/routines/${typeUrl}/${exerciseId}/copy?date=${date}`

    try {
      const { headersStates } = rootState
      commit('setLoading', true)
      await api.post(url)
      await dispatch('getExercises', { participantId: participantId })
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'Item pasted!', {
        root: true,
      })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async changeParticipantExercise(
    { rootState, commit, dispatch },
    { participantId, type, exerciseId, data, action = 'changed' },
  ) {
    let typeUrl = ''
    let transformedData = { ...data }
    let label = ''
    switch (type) {
      case 'EXERCISE':
        typeUrl = 'exercises'
        label = 'Exercise'
        break
      case 'SERIES':
        typeUrl = 'series'
        label = 'Circuit '
        delete transformedData.exercises
        delete transformedData.exerciseDefinitions
        transformedData.exercises = [...data.exerciseDefinitions]
        if (!transformedData.restTimeAfterExercise)
          transformedData.restTimeAfterExercise = 0
        break
      case 'ACTIVITY':
        typeUrl = 'activities'
        label = 'Activity'
        break
      default:
        label = 'Exercise'
        typeUrl = 'exercises'
        break
    }

    let url = `/participants/${participantId}/routines/${typeUrl}/${exerciseId}`

    const {
      media = null,
      additionalMedia = null,
      currentAdditionalMedia,
      currentMedia,
      ...rest
    } = transformedData

    const model = { ...rest }

    try {
      const { headersStates } = rootState

      commit('setLoading', true)
      let formData = new FormData()
      let file = null

      if (!media?.file && !media?.mlSourceResourceId && !media?.remove) {
        // do not change thumbnail
        model.media = null
      } else if (media?.mlSourceResourceId) {
        // select new thumbnail from media library
        model.media = { mlSourceResourceId: media?.mlSourceResourceId }
      } else if (media?.remove) {
        // remove thumbnail
        model.media = { remove: true }
      } else {
        // upload new thumbnail from local device
        file = media.file
      }

      // Additional Media
      let additionalFile = null

      if (
        !additionalMedia?.file &&
        !additionalMedia?.mlSourceResourceId &&
        !additionalMedia?.remove
      ) {
        // do not change thumbnail
        model.additionalMedia = null
      } else if (additionalMedia?.mlSourceResourceId) {
        // select new thumbnail from media library
        model.additionalMedia = {
          mlSourceResourceId: additionalMedia?.mlSourceResourceId,
        }
      } else if (additionalMedia?.remove) {
        // remove thumbnail
        model.additionalMedia = { remove: true }
      } else {
        // upload new thumbnail from local device
        additionalFile = additionalMedia.file
      }

      formData.append(
        'model',
        new Blob([JSON.stringify(model)], {
          type: 'application/json',
        }),
      )

      if (file) {
        formData.append(
          'file',
          new Blob([file], {
            type: 'application/octet-stream',
          }),
          media?.file?.name || file.name,
        )
      }

      if (additionalFile) {
        formData.append(
          'additionalFile',
          new Blob([additionalFile], {
            type: 'application/octet-stream',
          }),
          additionalMedia?.file?.name || additionalFile.name,
        )
      }
      const response = await api.put(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'If-Match': headersStates.headersEtag[url],
        },
      })
      await dispatch('getExercises', { participantId: participantId })
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', `${label} ${action}!`, {
        root: true,
      })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async moveParticipantExercise(
    { rootState, commit, dispatch },
    { participantId, data, exerciseId },
  ) {
    const calendarUrl = `/participants/${participantId}/routines/calendar`
    const url = `/participants/${participantId}/routines/calendar/items/${exerciseId}`
    try {
      const { headersStates } = rootState

      commit('setLoading', true)

      delete data.title
      delete data.description
      const response = await api.put(url, data, {
        headers: {
          'If-Match': headersStates.headersEtag[calendarUrl],
        },
      })
      await dispatch('getExercises', { participantId: participantId })
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', `Item moved!`, { root: true })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async pasteParticipantExercises(
    { commit, dispatch, rootState },
    { participantId, exercises = [], dates = [] },
  ) {
    let url = `/participants/${participantId}/routines/items/copy`

    if (exercises.length < 1) {
      commit(
        'appStates/setSnackbarNotification',
        'You need to select at least one exercise!',
        {
          root: true,
        },
      )
      throw new Error('You need to select at least one exercise!')
    }

    if (dates.length < 1) {
      commit(
        'appStates/setSnackbarNotification',
        'You need to select at least one day!',
        {
          root: true,
        },
      )
      throw new Error('You need to select at least one day!')
    }

    try {
      const { headersStates } = rootState
      commit('setLoading', true)
      await api.post(url, { items: exercises, date: dates })
      await dispatch('getExercises', { participantId: participantId })
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'Exercises pasted!', {
        root: true,
      })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async pasteParticipantExercisesDay(
    { commit, dispatch, rootState },
    { participantId, copyDate, pasteDates = [] },
  ) {
    let url = `/participants/${participantId}/routines/calendar/items/date/${copyDate}/copy`

    if (!copyDate) throw new Error('You need select day to copy!')
    if (pasteDates.length < 1) {
      commit(
        'appStates/setSnackbarNotification',
        'You need to select at least one day!',
        {
          root: true,
        },
      )
      throw new Error('You need to select at least one day!')
    }

    try {
      const { headersStates } = rootState
      commit('setLoading', true)
      await api.post(url, { date: [...pasteDates] })
      await dispatch('getExercises', { participantId: participantId })
      commit('setLoading', false)
      commit('appStates/setSnackbarNotification', 'Day pasted!', {
        root: true,
      })
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async getExercisesReport(
    { commit, state, dispatch },
    { participantId, params, loader = true },
  ) {
    params = params || state.exercisesFilters

    const url = `/participants/${participantId}/routines/report`

    try {
      if (loader) commit('setLoading', true)
      const response = await api.get(url, { params })
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      commit('setExercisesReport', response.data)
      commit('setLoading', false)
    } catch (error) {
      commit('setLoading', false)
    }
  },
  async getExercisesReportTotals(
    { commit, state, dispatch },
    { participantId },
  ) {
    const url = `/participants/${participantId}/routines/report/total`

    try {
      const response = await api.get(url)
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      commit('setExercisesReportTotals', response.data)
      commit('setLoading', false)
    } catch (error) {
      commit('setLoading', false)
    }
  },
  async getExercisesDayReport(
    { commit, state, dispatch },
    { participantId, date },
  ) {
    const url = `/participants/${participantId}/routines/report/day/${date}`

    try {
      let response = await api.get(url)
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      response.data.date = date
      commit('setExercisesDayReport', response.data)
      commit('setLoading', false)
    } catch (error) {
      commit('setLoading', false)
    }
  },
  async getExercisesCalendar(
    { commit, state, dispatch },
    { participantId, params, loader = true },
  ) {
    params = params || state.exercisesFilters

    const url = `/participants/${participantId}/routines/calendar/preview`

    try {
      if (loader) commit('setLoading', true)
      const response = await api.get(url, { params })
      commit(
        'headersStates/setHeadersEtag',
        { headers: response.headers, url },
        { root: true },
      )
      commit('setExercisesCalendar', response.data)
      commit('setLoading', false)
    } catch (error) {
      commit('setLoading', false)
    }
  },
  async generateRiseMap(
    { commit, dispatch },
    { participantId, language = 'en', from, to, status, empty = false },
  ) {
    if (!participantId) {
      console.error('No participantId in generateRiseMap')
      return null
    }

    const params = new URLSearchParams()
    let url = `/participants/${participantId}/swaps/generate-pdf`
    if (empty) {
      params.append('template', true)
      params.append('lang', language)
    } else {
      if (['NEW', 'IN_PROGRESS', 'DONE', 'REJECTED'].includes(status)) {
        params.append('statusFilter', status)
      }
      if (from) params.append('from', from)
      if (to) params.append('to', to)
    }

    try {
      const response = await api.post(url, null, {
        params,
        responseType: 'blob',
      })
      return response.data
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      return null
    }
  },
  async getSwaps(
    { commit, dispatch },
    { participantId, from = null, to = null, status = null },
  ) {
    if (!participantId) {
      console.error('No participantId in getSwaps')
      return
    }
    let url = `/participants/${participantId}/swaps`

    const params = new URLSearchParams()
    if (['NEW', 'IN_PROGRESS', 'DONE', 'REJECTED'].includes(status)) {
      params.append('status', status)
    }
    if (from) params.append('from', from)
    if (to) params.append('to', to)

    try {
      commit('setSwapsLoading', true)
      const response = await api.get(url, { params })
      commit('setSwaps', response.data.items)
      commit('setSwapsLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setSwapsLoading', false)
    }
  },
  async getSwap({ commit, state, dispatch }, { participantId, swapId }) {
    if (!participantId || !swapId) {
      console.error(
        `No params in getSwap: ${participantId ? '' : 'participantId'} ${
          swapId ? '' : 'swapId'
        }`,
      )
      return
    }
    const url = `/participants/${participantId}/swaps/${swapId}`
    try {
      commit('setSwapLoading', true)
      const response = await api.get(url)
      // commit(
      //   'headersStates/setHeadersEtag',
      //   { headers: response.headers, url },
      //   { root: true },
      // )
      commit('setSwap', response.data)
      commit('setSwapLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setSwap', false)
      commit('setSwapLoading', false)
    }
  },
  async addSwap({ commit, dispatch }, { participantId, data }) {
    try {
      // commit('setSwapsLoading', true)
      let formData = new FormData()

      const model = {
        description: data?.description || '',
        goal: data?.goal || '',
        tactic: data?.tactic || '',
        repeatGoal: data?.repeatGoal || 1,
        impactLevel: data?.impactLevel || '',
        difficultLevel: data?.difficultLevel || '',
      }

      let file = null

      if (data?.thumbnailValue === null) {
        // do not change thumbnail
        model.media = null
      } else if (data?.thumbnailValue?.mlSourceResourceId) {
        // select new thumbnail from media library
        model.media = {
          mlSourceResourceId: data?.thumbnailValue?.mlSourceResourceId,
        }
      } else if (data?.thumbnailValue?.remove) {
        // remove thumbnail
        model.media = { remove: true }
      } else {
        // upload new thumbnail from local device
        file = data.thumbnailValue
      }

      if (file) {
        formData.append(
          'file',
          new Blob([file.file], {
            type: 'application/octet-stream',
          }),
          file.name,
        )

        // remove attach ml file if there is file upload from computer included
        delete model['media']
      }

      formData.append(
        'model',
        new Blob([JSON.stringify(model)], {
          type: 'application/json',
        }),
      )

      const response = await api.post(
        `/participants/${participantId}/swaps`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      // commit('setSwapsLoading', false)
      // dispatch('userStates/getTasks', {}, { root: true })
      return response
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      // commit('setSwapsLoading', false)
      return false
    }
  },
  async updateSwap({ commit, dispatch }, { participantId, swapId, data }) {
    try {
      commit('setSwapLoading', true)

      const model = {
        description: data?.description || '',
        goal: data?.goal || '',
        tactic: data?.tactic || '',
        repeatGoal: data?.repeatGoal || 1,
        impactLevel: data?.impactLevel || '',
        difficultLevel: data?.difficultLevel || '',
        status: data?.status,
      }

      const response = await api.put(
        `/participants/${participantId}/swaps/${swapId}`,
        model,
      )
      commit('setSwapLoading', false)
      // dispatch('userStates/getTasks', {}, { root: true })
      return response
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setSwapLoading', false)
      return false
    }
  },
  async removeSwap({ commit, dispatch }, { participantId, swapId }) {
    try {
      commit('setSwapLoading', true)
      await api.delete(`/participants/${participantId}/swaps/${swapId}`)
      commit('setSwapLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setSwapLoading', false)
    }
  },
  async progressSwap({ commit, dispatch }, { participantId, swapId, date }) {
    try {
      commit('setSwapLoading', true)
      await api.post(
        `/participants/${participantId}/swaps/${swapId}/progress`,
        {
          date,
        },
      )
      commit('setSwapLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setSwapLoading', false)
    }
  },
  async addSwapEntry({ commit, dispatch }, { participantId, swapId, data }) {
    try {
      // commit('setSwapsLoading', true)
      let formData = new FormData()

      const model = {
        impactLevel: data?.impactLevel || '',
        difficultLevel: data?.difficultLevel || '',
      }

      if (data?.content) {
        model.content = data.content
      }

      let file = null

      if (data?.thumbnailValue === null) {
        // do not change thumbnail
        model.media = null
      } else if (data?.thumbnailValue?.mlSourceResourceId) {
        // select new thumbnail from media library
        model.media = {
          mlSourceResourceId: data?.thumbnailValue?.mlSourceResourceId,
        }
      } else if (data?.thumbnailValue?.remove) {
        // remove thumbnail
        model.media = { remove: true }
      } else {
        // upload new thumbnail from local device
        file = data.thumbnailValue
      }

      if (file) {
        formData.append(
          'file',
          new Blob([file.file], {
            type: 'application/octet-stream',
          }),
          file.name,
        )

        // remove attach ml file if there is file upload from computer included
        delete model['media']
      }

      formData.append(
        'model',
        new Blob([JSON.stringify(model)], {
          type: 'application/json',
        }),
      )

      const response = await api.post(
        `/participants/${participantId}/swaps/${swapId}/entry`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      )
      // commit('setSwapsLoading', false)
      // dispatch('userStates/getTasks', {}, { root: true })
      return response
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      // commit('setSwapsLoading', false)
      return false
    }
  },
  async getScreeners({ state, commit, dispatch }, { participantId }) {
    try {
      commit('setLoading', true)
      const response = await api.get(`/participants/${participantId}/surveys`)
      commit('setScreeners', response.data.items)
      commit('setPagination', response.data._meta)
      commit('setLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async getScreener(
    { state, commit, dispatch },
    { participantId, screenerId },
  ) {
    try {
      commit('setLoading', true)
      const response = await api.get(
        `/participants/${participantId}/surveys/${screenerId}`,
      )
      commit('setScreener', response.data)
      commit('setLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async getScreenerProgress(
    { state, commit, dispatch },
    { participantId, screenerId },
  ) {
    try {
      const response = await api.get(
        `/participants/${participantId}/surveys/${screenerId}/progress`,
      )
      commit('setScreenerProgress', response.data)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
    }
  },
  async getParticipantsDashboard(
    { state, commit, dispatch },
    { range = '', showAll = false, profileId = null, isCompanyAdmin = false },
  ) {
    const params = new URLSearchParams()
    if (['today', 'week', 'month', 'year'].includes(range)) {
      params.append('filter', range)
    }

    params.append('showAll', showAll === true)

    try {
      commit('setLoading', true)
      let response = null
      if (profileId === null && !isCompanyAdmin) {
        response = await api.get('/participants/dashboard', { params })
      } else {
        const advisorFullName = state.currentAdvisorDashboardName
        if (advisorFullName) {
          commit('appStates/setBreadcrumbCurrentView', advisorFullName, {
            root: true,
          })
        }
        response = await api.get(
          `/advisors/${profileId}/participants/dashboard`,
          {
            params,
          },
        )
      }
      commit('setParticipantsDashboard', response.data)
      commit('setLoading', false)
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
      commit('setLoading', false)
    }
  },
  async getParticipantCourses({ commit, dispatch }, { participantId, status }) {
    try {
      if (!participantId) return
      commit('setLoading', true)
      const response = await api.get(`/participants/${participantId}/courses`, {
        params: {
          status,
        },
      })
      commit('setCourses', response?.data?.content || [])
      commit('setLoading', false)
    } catch (error) {
      commit('setLoading', false)
      dispatch('appStates/setErrorMessage', error, { root: true })
    }
  },
  async getParticipantCourseSteps(
    { commit, dispatch },
    { participantId, courseId },
  ) {
    if (!courseId) return

    try {
      commit('setLoading', true)
      const response = await api.get(
        `/participants/${participantId}/courses/${courseId}/steps`,
      )
      commit('setCurrentCourseSteps', response?.data?.content || [])
      commit('setLoading', false)
    } catch (error) {
      commit('setLoading', false)
      dispatch('appStates/setErrorMessage', error, { root: true })
    }
  },
  async unassignParticipantFromCourse(
    { commit, dispatch },
    { participantId, courseId, status },
  ) {
    try {
      await api.post(
        `/participants/${participantId}/courses/${courseId}/unassign`,
      )

      debounce(
        () => {
          dispatch('getParticipantCourses', { participantId, status })
        },
        500,
        'getParticipantCoursesAfterUnassign',
      )
      commit(
        'appStates/setSnackbarNotification',
        'Participants unassigned from the Course!',
        {
          root: true,
        },
      )
    } catch (error) {
      dispatch('appStates/setErrorMessage', error, { root: true })
    }
  },
}
